import {createMultiStyleConfigHelpers, defineCssVars} from '@chakra-ui/styled-system';
import {useMultiStyleConfig} from '@chakra-ui/system';
import {mode} from '@chakra-ui/theme-tools';

import {semantic as semanticDark, components as componentsDark} from '../../design-tokens/dark';
import {core, semantic, components} from '../../design-tokens/light';
import {getCoreValueAsThemeToken} from '../types';

const parts = [`stepper`, `step`, `title`, `description`, `indicator`, `separator`, `icon`, `number`] as const;
const {gap: $gap, ...rest} = defineCssVars(`stepper`, [`gap`, `accent-color`]);
export type StepperParts = (typeof parts)[number];

const styleHelper = createMultiStyleConfigHelpers(parts);

const cssVars = {
  [$gap.variable]: core.space[`space-050`],
  [rest[`accent-color`].variable]: `colors.${getCoreValueAsThemeToken(core, `colors.brand.primary.600`)}`,
};

const baseStyle = styleHelper.definePartsStyle(props => ({
  indicator: {
    userSelect: `none`,
    borderColor: mode(semantic.colors.stroke.primary, semanticDark.colors.stroke.primary)(props),
    background: mode(semantic.colors.background.primary, semanticDark.colors.background.primary)(props),
    [`&[data-status=active]`]: {
      borderWidth: `1px`,
      borderColor: mode(semantic.colors.stroke[`brand-medium`], semanticDark.colors.stroke[`brand-medium`])(props),
    },
    [`&[data-status=complete]`]: {
      borderColor: mode(semantic.colors.stroke.primary, semanticDark.colors.stroke.primary)(props),
      background: mode(semantic.colors.background.secondary, semanticDark.colors.background.secondary)(props),
      borderWidth: `1px`,
    },
    [`&[data-status=incomplete]`]: {
      borderWidth: `1px`,
    },
    [`&[data-clickable=true]:hover`]: {
      borderColor: mode(semantic.colors.stroke.state.hover, semanticDark.colors.stroke.state.hover)(props),
      [`&[data-status=complete]`]: {
        borderColor: mode(
          components.colors.components[`form-control`][`stroke-hover`],
          componentsDark.colors.components[`form-control`][`stroke-hover`],
        )(props),
        background: mode(semantic.colors.background.primary, semanticDark.colors.background.primary)(props),
      },
    },
    _active: {},
    [`&[data-clickable=true]`]: {
      [`&:not([data-status=complete])`]: {
        pointerEvents: `none`,
        cursor: `not-allowed`,
      },
      [`&[data-status=complete]`]: {
        cursor: `pointer`,
      },
    },
  },
  stepper: {
    ...cssVars,
    gap: $gap.reference,
  },
  step: {
    gap: $gap.reference,
    backgroundColor: `transparent`,
  },

  separator: {
    background: mode(semantic.colors.stroke.primary, semanticDark.colors.stroke.primary)(props),
  },
  number: {
    color: mode(semantic.colors.text.primary, semanticDark.colors.text.primary)(props),
    fontWeight: `bold`,
  },
}));

const StepperBaseTheme = styleHelper.defineMultiStyleConfig({
  baseStyle,
  sizes: {},
  defaultProps: {
    colorScheme: `brand.primary`,
  },
});

const StepperTheme = StepperBaseTheme;

export default StepperTheme;

export const useStepperStyleConfig = (part?: StepperParts, props?: Parameters<typeof useMultiStyleConfig>[1]) => {
  return useMultiStyleConfig(`Stepper${part ? `.${part}` : ``}`, props ?? {});
};
